import React, { useState, useEffect } from 'react';
import { contactService } from '../services/api.service';
import {
  MessageSquare,
  Search,
  RefreshCw,
  Mail,
  User,
  Clock,
  CheckCircle2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Filter,
  Eye,
  Send
} from 'lucide-react';

const ITEMS_PER_PAGE = 10;

const ContactMessages = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMessage, setSelectedMessage] = useState(null);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const res = await contactService.getMessages();
      setMessages(res.messages || []);
    } catch (err) {
      console.warn("Failed to load contact messages:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const handleUpdateStatus = async (id, status) => {
    try {
      await contactService.updateStatus(id, status);
      setMessages(prev => prev.map(m => m.id === id ? { ...m, status } : m));
      if (selectedMessage && selectedMessage.id === id) {
        setSelectedMessage(prev => ({ ...prev, status }));
      }
    } catch (err) {
      alert("Failed to update status.");
    }
  };

  // Metrics Calculations
  const totalCount = messages.length;
  const unreadCount = messages.filter(m => m.status === 'unread').length;
  const repliedCount = messages.filter(m => m.status === 'replied').length;

  // Filtering Logic
  const filteredMessages = messages.filter(m => {
    const term = search.toLowerCase();
    const matchesSearch =
      (m.full_name && m.full_name.toLowerCase().includes(term)) ||
      (m.email && m.email.toLowerCase().includes(term)) ||
      (m.subject && m.subject.toLowerCase().includes(term)) ||
      (m.message && m.message.toLowerCase().includes(term));

    const matchesStatus = statusFilter === 'ALL' || m.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  // Pagination Math
  const totalPages = Math.ceil(filteredMessages.length / ITEMS_PER_PAGE) || 1;
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;
  const currentMessages = filteredMessages.slice(indexOfFirstItem, indexOfLastItem);

  const renderStatusBadge = (status) => {
    const s = status ? status.toLowerCase() : 'unread';
    let bg = '#fee2e2';
    let color = '#dc2626';
    let label = 'Unread';

    if (s === 'read') {
      bg = '#eff6ff';
      color = '#2563eb';
      label = 'Read';
    } else if (s === 'replied') {
      bg = '#dcfce7';
      color = '#16a34a';
      label = 'Replied';
    }

    return (
      <span style={{ fontSize: '11px', fontWeight: 800, color, background: bg, padding: '3px 10px', borderRadius: '6px', textTransform: 'capitalize' }}>
        {label}
      </span>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Top Banner & Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <h2 style={{ fontSize: '20px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px 0' }}>
            Customer Contact Messages ({unreadCount} Unread)
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
            Messages submitted via storefront contact form & dispatched to sento@gmail.com.
          </p>
        </div>

        <button onClick={fetchMessages} className="btn-secondary" style={{ width: 'auto', padding: '0 16px', height: '40px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <RefreshCw size={15} />
          <span>Refresh Messages</span>
        </button>
      </div>

      {/* METRICS OVERVIEW CARDS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '16px' }}>
        
        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Total Inquiries</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#eff6ff', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <MessageSquare size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{totalCount}</div>
          <div style={{ fontSize: '12px', color: '#3b82f6', fontWeight: 700, marginTop: '4px' }}>Received Contact Submissions</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unread Messages</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#fef2f2', color: '#dc2626', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <AlertCircle size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{unreadCount}</div>
          <div style={{ fontSize: '12px', color: '#dc2626', fontWeight: 700, marginTop: '4px' }}>Requires Attention</div>
        </div>

        <div className="table-card-box" style={{ padding: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontSize: '12px', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Replied Messages</span>
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: '#ecfdf5', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle2 size={16} />
            </div>
          </div>
          <div style={{ fontSize: '26px', fontWeight: 900, color: '#0f172a' }}>{repliedCount}</div>
          <div style={{ fontSize: '12px', color: '#10b981', fontWeight: 700, marginTop: '4px' }}>Resolved & Responded</div>
        </div>

      </div>

      {/* FILTER & SEARCH BAR */}
      <div className="table-card-box" style={{ padding: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px', flexWrap: 'wrap', gap: '16px' }}>
          
          <div className="header-search-wrapper" style={{ width: '360px', display: 'block' }}>
            <input
              type="text"
              className="header-search-input"
              placeholder="Search by customer name, email, or message..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />
            <Search className="header-search-icon" size={16} />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Filter size={14} color="#64748b" />
            <span style={{ fontSize: '12px', color: '#64748b', fontWeight: 700 }}>Status:</span>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
              style={{ background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '6px 12px', fontSize: '13px', fontWeight: 700, outline: 'none' }}
            >
              <option value="ALL">All Messages</option>
              <option value="unread">Unread</option>
              <option value="read">Read</option>
              <option value="replied">Replied</option>
            </select>
          </div>

        </div>

        {/* MESSAGES TABLE */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0', color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>CUSTOMER / EMAIL</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>SUBJECT</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>MESSAGE SNIPPET</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>STATUS</th>
                <th style={{ padding: '12px 16px', fontWeight: 800 }}>RECEIVED DATE</th>
                <th style={{ padding: '12px 16px', fontWeight: 800, textAlign: 'right' }}>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading messages...</td></tr>
              ) : currentMessages.length === 0 ? (
                <tr><td colSpan={6} style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>No contact messages found.</td></tr>
              ) : (
                currentMessages.map((m) => (
                  <tr key={m.id} style={{ borderBottom: '1px solid #f1f5f9', background: m.status === 'unread' ? '#fffbfb' : '#ffffff' }}>
                    <td style={{ padding: '14px 16px' }}>
                      <div style={{ fontWeight: 800, color: '#0f172a' }}>{m.full_name}</div>
                      <a href={`mailto:${m.email}`} style={{ fontSize: '12px', color: '#dc2626', textDecoration: 'none', fontWeight: 600 }}>{m.email}</a>
                    </td>
                    <td style={{ padding: '14px 16px', fontWeight: 700, color: '#0f172a' }}>
                      {m.subject || 'General Inquiry'}
                    </td>
                    <td style={{ padding: '14px 16px', color: '#64748b', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {m.message}
                    </td>
                    <td style={{ padding: '14px 16px' }}>
                      {renderStatusBadge(m.status)}
                    </td>
                    <td style={{ padding: '14px 16px', color: '#64748b', fontSize: '12px' }}>
                      {new Date(m.created_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })}
                    </td>
                    <td style={{ padding: '14px 16px', textAlign: 'right' }}>
                      <button
                        onClick={() => {
                          setSelectedMessage(m);
                          if (m.status === 'unread') handleUpdateStatus(m.id, 'read');
                        }}
                        style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 800, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                      >
                        <Eye size={14} />
                        <span>View</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '20px', paddingTop: '16px', borderTop: '1px solid #f1f5f9' }}>
            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <ChevronLeft size={16} />
                <span>Previous</span>
              </button>
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                style={{ padding: '6px 12px', border: '1px solid #e2e8f0', borderRadius: '6px', background: '#ffffff', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', opacity: currentPage === totalPages ? 0.5 : 1, display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', fontWeight: 600 }}
              >
                <span>Next</span>
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

      </div>

      {/* VIEW MESSAGE MODAL */}
      {selectedMessage && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(4px)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ background: '#ffffff', borderRadius: '24px', width: '100%', maxWidth: '600px', padding: '32px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #f1f5f9', paddingBottom: '16px' }}>
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: 900, color: '#0f172a', margin: '0 0 2px 0' }}>
                  Inquiry from {selectedMessage.full_name}
                </h3>
                <span style={{ fontSize: '12px', color: '#64748b' }}>
                  Received {new Date(selectedMessage.created_at).toLocaleString()}
                </span>
              </div>
              <button onClick={() => setSelectedMessage(null)} style={{ background: 'none', border: 'none', fontSize: '18px', fontWeight: 800, cursor: 'pointer', color: '#64748b' }}>✕</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', fontSize: '13.5px' }}>
              <div><strong style={{ color: '#0f172a' }}>Email:</strong> <a href={`mailto:${selectedMessage.email}`} style={{ color: '#dc2626' }}>{selectedMessage.email}</a></div>
              <div><strong style={{ color: '#0f172a' }}>Subject:</strong> {selectedMessage.subject || 'General Inquiry'}</div>
              <div><strong style={{ color: '#0f172a' }}>Status:</strong> {renderStatusBadge(selectedMessage.status)}</div>
              
              <div style={{ marginTop: '8px' }}>
                <strong style={{ color: '#0f172a', display: 'block', marginBottom: '6px' }}>Message:</strong>
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '16px', color: '#334155', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  {selectedMessage.message}
                </div>
              </div>
            </div>

            {/* Quick Status Toggle Actions */}
            <div style={{ display: 'flex', gap: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9' }}>
              <button
                onClick={() => handleUpdateStatus(selectedMessage.id, 'unread')}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', background: selectedMessage.status === 'unread' ? '#fef2f2' : '#ffffff', color: selectedMessage.status === 'unread' ? '#dc2626' : '#334155', fontWeight: 700, fontSize: '12.5px', cursor: 'pointer' }}
              >
                Mark Unread
              </button>
              <button
                onClick={() => handleUpdateStatus(selectedMessage.id, 'read')}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: '1px solid #cbd5e1', background: selectedMessage.status === 'read' ? '#eff6ff' : '#ffffff', color: selectedMessage.status === 'read' ? '#2563eb' : '#334155', fontWeight: 700, fontSize: '12.5px', cursor: 'pointer' }}
              >
                Mark Read
              </button>
              <button
                onClick={() => handleUpdateStatus(selectedMessage.id, 'replied')}
                style={{ flex: 1, padding: '10px', borderRadius: '8px', border: 'none', background: '#dc2626', color: '#ffffff', fontWeight: 800, fontSize: '12.5px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
              >
                <Send size={14} />
                <span>Mark Replied</span>
              </button>
            </div>

          </div>
        </div>
      )}

    </div>
  );
};

export default ContactMessages;
